import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGrants } from '../../auth/useCurrentUser'
import { GRANTS } from '../../auth/grants'
import Button from '../../components/ui/Button'
import { useToast } from '../../components/ui/ToastProvider'
import { businessToday, formatDateTime } from '../../lib/datetime'
import StandaloneActionFormDialog from '../actions/components/StandaloneActionFormDialog'
import { useCreateStandaloneAction } from '../actions/hooks/useActions'
import type { StandaloneActionCreateRequest } from '../actions/types'
import QuickNoteDialog from '../todo/components/QuickNoteDialog'
import { useQuickAddNote, type QuickNoteArgs } from '../todo/hooks/useTodoLists'
import { useProxyMode } from '../proxy/useProxyMode'
import DashboardFilters from './components/DashboardFilters'
import { useActionDashboard } from './hooks/useActionDashboard'
import { useInvalidateDashboard } from './hooks/useDashboardActions'
import { useDashboardManagers, useDashboardScope } from './hooks/useDashboardManagers'
import { useMyReport } from './hooks/useMyReport'
import ManagerReport from './ManagerReport'
import MyReport from './MyReport'
import {
  DASHBOARD_PERIODS,
  DEFAULT_MY_PERIOD,
  DEFAULT_PERIOD,
  EMPTY_MY_QUERY,
  EMPTY_QUERY,
  MY_REPORT_PERIODS,
  viewFor,
  isDefaultQuery,
  isQueryReady,
  type DashboardQuery,
  type DashboardView,
} from './types'
import './home.css'

/**
 * Anasayfa kabuğu (068 — analiz "Dashboardlar Arası Geçiş"): iki panoyu (Yönetici Raporu / Raporum)
 * barındırır, aralarındaki geçişi yönetir ve <b>ortak</b> parçaları (başlık, filtre çubuğu, "Son
 * Güncelleme", hata satırı) tek yerde tutar.
 *
 * <p><b>Kullanıcı TEK pano görür ve panolar arası geçiş YOKTUR</b> (kullanıcı isteği 2026-08-21): hangi
 * panonun açılacağını <b>kapsam yetkisi</b> belirler ({@code viewFor}) — o yetkiyi taşıyan üç rol
 * (Genel Müdür · Özel Kalem Müdürü · Özel Kalem Personeli) Yönetici Raporu'nu, diğer kullanıcılar
 * Raporum'u görür. 068 §K14'ün `?view=` parametresi ve
 * 077 §K7'nin "…Geçiş Yap" butonu bu turda <b>kaldırıldı</b>: seçilecek bir pano kalmadığı için korunacak
 * bir durum da yok.
 *
 * <p><b>Pano seçimi `DASHBOARD_GM_SCOPE` / `DASHBOARD_YKB_SCOPE` yetkisinden, kişi kümesi `scope.kind`'tan okunur</b>
 * (2026-08-23; yetkinin kaynağı 2026-10-13'te kapsam yanıtından `/me`'ye alındı — gerekçe ve ölçüm
 * `viewFor`'da): ikisi ayrı kararlardır. Pano verisi bu yüzden kapsamı BEKLEMEZ; iki istek paralel gider,
 * kapsam yalnız kişi filtresinin başlığını ve pasifliğini belirler. Kapsam çözümü fail-soft'tur — HR'da Genel Müdür tanımı bulunamazsa daha dar
 * bir kümeye iner; pano seçimi ise yetkidir ve daralmaz. Pano `kind`'a bağlıyken, yetkisi olan bir
 * Genel Müdür HR verisi eksik olduğu için <b>sessizce</b> Raporum'a düşüyordu (THY test bildirimi).
 * Daralma için bir süre gösterilen uyarı bandı kullanıcı isteğiyle <b>kaldırıldı</b> (2026-08-24):
 * pano yetkiye göre doğru açılır, kapsamın darlığı ekranda ayrıca duyurulmaz.
 *
 * <p><b>Yalnız görünen pano veri çeker</b> (`enabled`): iki hook da mount edilir ama pasif olan istek
 * atmaz — kullanıcının panosu olmayan uç hiç çağrılmaz.
 *
 * <p><b>İki pano da 30 saniyede bir kendiliğinden tazelenir</b> ve <b>hiçbirinde "Yenile" butonu
 * yoktur</b> (kullanıcı isteği 2026-08-19). 077 §K9 butonu yalnız Yönetici Raporu'ndan kaldırmış,
 * Raporum'da bırakmıştı; iki pano arasındaki bu fark kaldırıldı. Tazelemenin görünen kanıtı üstteki
 * <b>"Son Güncelleme"</b> damgasıdır — sunucudan gelen `generatedAt` her turda ilerler.
 *
 * <p><b>Zaman filtresi panoya özeldir</b> (2026-08-19 revizyonu — 068'in "ortak filtre" kararı tersine
 * döndü): iki panonun <b>varsayılanı ve seçenek listesi farklıdır</b> (Yönetici Raporu Son 1 Yıl + altı
 * seçenek, Raporum Son 1 Ay + dört seçenek), bu yüzden her pano kendi sorgu durumunu tutar. Tek durum
 * paylaşılsaydı Raporum'a her geçişte Yönetici Raporu'nun dönemi taşınır ve kullanıcı Raporum'un
 * varsayılanını hiç görmezdi. Kişi filtresi yalnız Yönetici Raporu'nda görünür (analiz §2.13).
 *
 * <p><b>"Aksiyon Oluştur" (077 §K7):</b> yalnız Yönetici Raporu'nu gören kullanıcıda vardır — koşul
 * panonun kendisidir ({@code managerView}), ayrı bir kapsam kontrolü değil. Ayrım rol kimliğinden değil,
 * sunucunun zaten döndürdüğü {@code scope} yanıtından yapılır (068 §K3 — yetki mantığı istemciye
 * taşınmaz). Daha önce {@code scope.kind === 'GENERAL_MANAGER'} aranıyordu; bu, kapsamı daralan yetkili
 * kullanıcıda butonu panodan bağımsız olarak kaybediyordu.
 *
 * <p><b>Kapsam çözülene kadar hiçbir pano çizilmez:</b> hangi panonun açılacağını bilmeden açmak,
 * standart kullanıcıya bir an Yönetici Raporu gösterip Raporum'a atlamak (ve boşa bir pano isteği atmak)
 * olurdu.
 */
/**
 * Yönetici Raporu'nda "Son Güncelleme"nin solundaki gün sayacının başlangıcı (kullanıcı isteği
 * 2026-09-28: "11 Nisan'dan günümüze kadar olan süre"; 2026-10-21'de 9 Nisan'a çekildi). Yerel gün
 * olarak kurulur — {@link businessToday} de yerel gece yarısı döndürdüğü için fark tam gün çıkar.
 */
const DAY_COUNTER_START = new Date(2026, 3, 9)
const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * 9 Nisan'dan kurumsal bugüne (Europe/Istanbul) geçen gün sayısı; 9 Nisan'ın kendisi 0'dır.
 * `Math.round`: iki uç da TARAYICININ yerel gece yarısıdır — tarayıcı yaz saati uygulayan bir dilimdeyse
 * aradaki fark 23/25 saatlik bir gün içerebilir, kesirli sonuç tam güne yuvarlanır.
 */
function daysSinceCounterStart(): number {
  return Math.round((businessToday().getTime() - DAY_COUNTER_START.getTime()) / MS_PER_DAY)
}

export default function HomeDashboard() {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant, hasAnyGrant, isLoading: grantsLoading } = useGrants()
  // İki panonun sorgusu AYRI: varsayılan dönemleri ve seçenek listeleri farklı (yukarıdaki not).
  const [managerQuery, setManagerQuery] = useState<DashboardQuery>(EMPTY_QUERY)
  const [myQuery, setMyQuery] = useState<DashboardQuery>(EMPTY_MY_QUERY)
  const [createOpen, setCreateOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)

  const people = useDashboardManagers()
  const scope = useDashboardScope()
  // Kapsam yalnız Yönetici Raporu'nun kişi filtresini besler; o filtre kapsam gelmeden çizilmez
  // (aşağıdaki `scopeReady`). Raporum'un filtresi kapsamı beklemez ama kişi alanı olmadığı için
  // buradaki yedek değeri hiç göstermez — değer yalnız tipi doldurur.
  const scopeKind = scope.data?.kind ?? 'SELF'
  // Hata hâlinde de devam edilir: kapsam çözülemezse pano yine açılır, yalnız kişi filtresi çizilir.
  const scopeReady = !scope.isPending

  // Panoyu YALNIZ yetki belirler; kullanıcının seçebileceği bir şey yok (geçiş kalktı). Yetki `/me`'den
  // gelir ve kapsamı beklemez (`viewFor`). `/me` yüklenmeden pano çizilmez: yetkiler yokken `hasGrant`
  // false döner (fail-closed) ve Yönetici Raporu'nu görecek kullanıcı bir an Raporum'u görürdü.
  const viewReady = !grantsLoading
  // İki kod da okunur — sunucunun `managerReport` kuralıyla aynı (`gmScope || ykbScope`).
  const view: DashboardView = viewFor(
    hasAnyGrant(GRANTS.DASHBOARD_GM_SCOPE, GRANTS.DASHBOARD_YKB_SCOPE),
  )

  const managerView = view === 'manager'
  /**
   * Etkin panonun filtre bağlamı — sorgu durumu, sıfırlama hedefi, varsayılan dönem ve seçenek listesi.
   * Beş ayrı `managerView ? … : …` yerine **tek** yerde çözülür: hem beşinin ayrışması imkânsız olur hem
   * de kabuğun bilişsel karmaşıklığı Sonar sınırının altında kalır (ölçüldü: 17 → 14).
   */
  const filters = managerView
    ? {
        query: managerQuery,
        setQuery: setManagerQuery,
        empty: EMPTY_QUERY,
        defaultPeriod: DEFAULT_PERIOD,
        periods: DASHBOARD_PERIODS,
      }
    : {
        query: myQuery,
        setQuery: setMyQuery,
        empty: EMPTY_MY_QUERY,
        defaultPeriod: DEFAULT_MY_PERIOD,
        // Raporum'un dönem listesi dört seçenektir (analiz) → "Özel Tarih Aralığı" da orada yoktur.
        periods: MY_REPORT_PERIODS,
      }
  const query = filters.query
  const dashboard = useActionDashboard(managerQuery, viewReady && managerView)
  const myReport = useMyReport(myQuery, viewReady && !managerView)
  const invalidateDashboard = useInvalidateDashboard()
  const createAction = useCreateStandaloneAction()
  const addNote = useQuickAddNote()
  /**
   * Kısıtlı Yetkili asistan Notlarım'ı okur ama yazamaz (126, kullanıcı bildirimi 21.10.2026);
   * "Not Ekle" bir YAZMA yoludur ve bu kipte hiç sunulmaz. Modülün kendisi görünür kalır — yetki
   * kodu izin listesinde durur, kapatılan yalnız yazma yüzeyleridir.
   */
  const { readOnly } = useProxyMode()

  const active = managerView ? dashboard : myReport
  const loading = active.isLoading
  const failed = active.isError && active.data == null
  const generatedAt = managerView ? dashboard.data?.generatedAt : myReport.data?.generatedAt

  // Özel tarih aralığı yarım kaldığında sorgu bilinçli olarak atılmaz (sunucu 400 dönerdi). Bu arada
  // panoyu ÇİZMEYİZ: bölümler "veri yok" boş durumlarına düşer ve kullanıcı daha tarih seçerken ekran
  // ona "kayıt bulunamadı" demiş olurdu. Yerine ne beklendiğini söyleyen tek satırlık bir yönerge.
  const awaitingRange = !isQueryReady(query)

  // Kişi filtresi yalnız Raporum'da hiç çizilmez (analiz §2.13). Yönetici Raporu'nda kişi kümesi boş
  // olsa bile alan durur, yalnız PASİF olur (analiz §1.3.3; analist kararı 2026-08-05).
  //
  // Sunucunun `filterVisible` alanına BİLİNÇLİ olarak bakılmaz (2026-08-23 review sorusu): o alan
  // "küme boş → alanı hiç gösterme" diyen §1.12.1'i taşır, ama analist 2026-08-05'te §1.3.3 lehine
  // karar verdi — alan görünür kalır, pasif çizilir. Koşula `filterVisible` eklenirse kapsamı daralmış
  // kullanıcıda filtre tümden kaybolur ve o karar sessizce geri alınmış olur.
  const showPeople = managerView

  const submitAction = (payload: StandaloneActionCreateRequest, files: File[]) => {
    createAction.mutate(
      { body: payload, files },
      {
        // Ek adımı patlasa da görev oluştu → uyarı gösterilir, pano yine tazelenir (bkz. ActionsPage).
        onSuccess: ({ filesFailed }) => {
          if (filesFailed) {
            toast.warn(t('actions.create.success'), t('actions.create.filesFailed'))
          } else {
            toast.success(t('actions.create.success'))
          }
          setCreateOpen(false)
          // Yeni aksiyon taban kümeye girer → donut, kişi grafiği ve alt tablo tazelenir.
          invalidateDashboard()
        },
        // Hata → merkezî toast (019).
      },
    )
  }

  /**
   * 123 — "Not Ekle": seçilen Notlarım listesine madde ve (varsa) dosyaları. Pano
   * tazelenmez: not görev kümesine girmez, panonun hiçbir sayısını değiştirmez.
   */
  const submitNote = (args: QuickNoteArgs) => {
    addNote.mutate(args, {
      onSuccess: ({ filesFailed }) => {
        if (filesFailed) {
          toast.warn(t('todo.quickNote.success'), t('todo.quickNote.filesFailed'))
        } else {
          toast.success(t('todo.quickNote.success'))
        }
        setNoteOpen(false)
      },
      // Hata → merkezî toast (019).
    })
  }

  return (
    <div className="page home-page">
      <div className="home-head">
        <div className="home-head-text">
          <h1 className="ui-page-header-title">
            {/* Yetkiler gelmeden başlık yazılmaz: yanlış panonun adını bir an göstermek olurdu. */}
            {!viewReady ? '' : managerView ? t('home.managerReport.title') : t('home.myReport.title')}
          </h1>
        </div>
        <div className="home-head-tools">
          {/* Yetki ayrıca aranır: asıl koruma backend'dedir, buton yalnız var olmayan bir yolu
              göstermemek için gizlenir (rol 5'te ACTION_CREATE canlıda mevcut — 2026-08-06). */}
          {viewReady && managerView && hasGrant(GRANTS.ACTION_CREATE) && (
            <Button
              type="button"
              className="home-create"
              icon="pi pi-plus"
              label={t('actions.create.new')}
              onClick={() => setCreateOpen(true)}
            />
          )}
          {/* 123 — "Not Ekle", Görev Oluştur'un yanında. Görev Oluştur'un aksine İKİ panoda da
              vardır: Notlarım kişiseldir, görünürlük koşulu pano değil yalnız modül yetkisidir.
              Kapsamı BEKLEMEZ. `viewReady` görünürlük için değil, Görev Oluştur'la aynı anda
              belirmesi için aranır: bu buton önce çıksaydı Görev Oluştur sonradan SOLUNA eklenir ve
              buton kayardı — o anda tıklayan kullanıcı yanlış pop-up'ı açabilirdi (MR !303). */}
          {viewReady && hasGrant(GRANTS.TODO_LIST_VIEW) && !readOnly && (
            <Button
              type="button"
              variant="secondary"
              className="home-create"
              icon="pi pi-pencil"
              label={t('todo.quickNote.open')}
              onClick={() => setNoteOpen(true)}
            />
          )}
          {/* Yönetici Raporu'nun kişi filtresi başlığını ve pasifliğini kapsamdan alır; kapsam pano
              verisiyle PARALEL gelir. Raporum'un filtresinde kişi alanı yoktur, kapsamı beklemez. */}
          {viewReady && (!managerView || scopeReady) && (
          <DashboardFilters
            query={query}
            onChange={filters.setQuery}
            onClear={() => filters.setQuery(filters.empty)}
            showClear={!isDefaultQuery(query, filters.defaultPeriod)}
            periods={filters.periods}
            showPeople={showPeople}
            people={people.data ?? []}
            scopeKind={scopeKind}
          />
          )}
        </div>
      </div>

      {/* Veri tazeliği bilgisi (sağa yaslı) — analiz §1.11; iki panoda da gösterilir (spec §K14). Gün
          sayacı YALNIZ Yönetici Raporu'nda (kullanıcı isteği 2026-09-28) — koşul panonun kendisidir. */}
      {generatedAt && (
        <div className="home-meta">
          {managerView && (
            <span className="home-days">
              {t('home.dayCount')}: {daysSinceCounterStart()}
            </span>
          )}
          <span className="home-updated">
            {t('home.lastUpdate')}: {formatDateTime(generatedAt)}
          </span>
        </div>
      )}

      {/* Kısmi hata: satır-içi mesaj (modal/alert değil); önceki veri varsa ekranda KALIR. */}
      {active.isError && (
        <p className="home-error" role="alert">
          {t('home.error')}
        </p>
      )}

      {awaitingRange && <p className="home-awaiting">{t('home.filters.awaitingRange')}</p>}

      {!viewReady && <p className="home-awaiting">{t('home.loading')}</p>}

      {viewReady &&
        !failed &&
        !awaitingRange &&
        (managerView ? (
          <ManagerReport data={dashboard.data} query={managerQuery} />
        ) : (
          <MyReport data={myReport.data} loading={loading} query={myQuery} />
        ))}

      <StandaloneActionFormDialog
        visible={createOpen}
        submitting={createAction.isPending}
        onSubmit={submitAction}
        onHide={() => setCreateOpen(false)}
      />

      <QuickNoteDialog
        visible={noteOpen}
        submitting={addNote.isPending}
        onSubmit={submitNote}
        onHide={() => setNoteOpen(false)}
      />
    </div>
  )
}
